import { Alert, Box, Button, Checkbox, FormControlLabel, Link, Typography } from '@mui/material';
import { useState } from 'react';
import PostingService from '../../services/PostingService';
import { taskErrorMessage } from '../../services/TaskTrackingService';
import AlsoPostingDialog from './AlsoPostingDialog';
import MarkReadyDialog from './MarkReadyDialog';
import { StepBox } from './TaskStepCard';
import { dayLabel } from './calendarUi';
import { platformChoices, platformLabel } from './taskFields';

/** One Posted tick, for one date. */
const PostedTick = ({ label, checked, onChange }) => (
    <FormControlLabel
        sx={{ display: 'flex' }}
        control={<Checkbox size="small" checked={checked} onChange={(e) => onChange(e.target.checked)} />}
        label={<Typography variant="body2">{label}</Typography>}
    />
);

/**
 * The drawer's Posting card, for PMs and admins (rev6 Part 11): Mark ready to post on a done video,
 * Add a platform when it has none, the Posted ticks, and Also posting on.
 */
const PostingSection = ({ task, posting, platforms, openDialog, onChanged }) => {
    const [marking, setMarking] = useState(false);
    const [also, setAlso] = useState(false);
    const [error, setError] = useState('');
    const video = task.contentType === 'VIDEO' && task.overallStatus !== 'CANCELLED';
    const doneVideo = video && task.overallStatus === 'COMPLETED';
    const secondDate = posting.secondDate;

    const tick = async (slot, posted) => {
        setError('');
        try {
            await PostingService.setPosted(task.id, slot, posted);
            onChanged();
        } catch (e) {
            setError(taskErrorMessage(e));
        }
    };

    const doneDetails = (title, fields) =>
        openDialog({
            usageName: 'TaskDoneDetailsDialog',
            title,
            submitLabel: 'Save',
            fields,
            submit: async (vals) => {
                await PostingService.setDoneDetails(task.id, { platform: vals.platform || null, date: vals.date || null });
                onChanged();
                return null;
            },
        });
    const dateField = { name: 'date', label: 'Posting date', type: 'date', initial: task.targetDate || '' };
    const addPlatform = () =>
        doneDetails('Add a platform', [
            {
                name: 'platform',
                label: 'Platform',
                type: 'select',
                options: platformChoices(platforms, null),
                required: true,
            },
            dateField,
        ]);
    const changeDate = () => doneDetails('Change posting date', [{ ...dateField, required: true }]);

    const empty = !posting.needsPlatform && !posting.needsReady && !posting.ready && !video;
    if (empty || (!task.platform && !posting.needsPlatform)) return null;

    const state = posting.posted ? 'Posted' : posting.ready ? 'Ready to post' : doneVideo ? 'Done, not marked ready yet' : null;
    return (
        <StepBox heading="Posting" line={state}>
            {posting.needsPlatform && (
                <Button variant="outlined" size="small" sx={{ mt: 1 }} onClick={addPlatform}>
                    Add a platform to post this
                </Button>
            )}
            {doneVideo && task.platform && (
                <Button variant={posting.ready ? 'outlined' : 'contained'} size="small" sx={{ mt: 1 }} onClick={() => setMarking(true)}>
                    {posting.ready ? 'Change ready languages' : 'Mark ready to post'}
                </Button>
            )}
            {posting.ready && (
                <Box sx={{ mt: 0.5 }}>
                    <PostedTick label={secondDate ? `Posted on ${platformLabel(platforms, task.platform)}` : 'Posted'} checked={posting.posted} onChange={(on) => tick(1, on)} />
                    {secondDate && (
                        <PostedTick label={`Posted on ${platformLabel(platforms, posting.secondPlatform)}`} checked={posting.secondPosted} onChange={(on) => tick(2, on)} />
                    )}
                </Box>
            )}
            {video && task.platform && (
                <Typography variant="body2" sx={{ mt: 0.5 }} component="div">
                    {secondDate && !posting.ready && `Also posting on ${platformLabel(platforms, posting.secondPlatform)}, ${dayLabel(secondDate)}. `}
                    {!posting.secondPosted && (
                        <Link component="button" variant="body2" onClick={() => setAlso(true)}>
                            {secondDate ? 'Change Also posting on' : 'Also posting on'}
                        </Link>
                    )}
                    {doneVideo && !posting.posted && (
                        <>
                            {' · '}
                            <Link component="button" variant="body2" onClick={changeDate}>
                                {task.targetDate ? 'Change posting date' : 'Set posting date'}
                            </Link>
                        </>
                    )}
                </Typography>
            )}
            {error && (
                <Alert severity="error" sx={{ mt: 1 }}>
                    {error}
                </Alert>
            )}
            {marking && (
                <MarkReadyDialog
                    taskId={task.id}
                    onClose={() => setMarking(false)}
                    onSaved={() => {
                        setMarking(false);
                        onChanged();
                    }}
                />
            )}
            {also && (
                <AlsoPostingDialog
                    task={task}
                    posting={posting}
                    platforms={platforms}
                    onClose={() => setAlso(false)}
                    onSaved={() => {
                        setAlso(false);
                        onChanged();
                    }}
                />
            )}
        </StepBox>
    );
};

export default PostingSection;
